import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import FoundationalEducationService from '../services/foundationalEducationService';
import { useAuth } from '../context/AuthContext';
import {
  DESCRIPTION_MAX,
  TITLE_MAX,
  URL_MAX,
  formToRequest,
  linkToRequest,
  nextSortOrder,
  planMove,
  shortUrl,
  sortLinks,
  validateLink,
} from '../utils/foundationalEducation';

/**
 * DV-310: Amway's Program Standards require its free Foundational Education materials to be
 * offered, labelled as such, wherever other business support materials are sold. Admins manage
 * the links here; once logged in, members see the active ones on the Foundational Education page,
 * in the nav and footer, and on every store, content and events page. Bilingual (EN + optional ES).
 */

const EMPTY_FORM = {
  titleEn: '',
  titleEs: '',
  descriptionEn: '',
  descriptionEs: '',
  urlEn: '',
  urlEs: '',
  active: true,
};

const linkToForm = (l) => ({
  titleEn: l.titleEn || '',
  titleEs: l.titleEs || '',
  descriptionEn: l.descriptionEn || '',
  descriptionEs: l.descriptionEs || '',
  urlEn: l.urlEn || '',
  urlEs: l.urlEs || '',
  active: !!l.active,
});

const UrlLink = ({ url, prefix }) => (
  <Tooltip title={url}>
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, wordBreak: 'break-all' }}
    >
      {prefix}{shortUrl(url)}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </Link>
  </Tooltip>
);

/* ═══════════════════════════════════════════════════════════════════════ */
const FoundationalEducationPage = () => {
  const { getAccessToken } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // A toggle or reorder is being saved: row actions wait for it.
  const [busy, setBusy] = useState(false);

  // Create / edit dialog. `editing` is the link being edited, null when creating.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // `quiet` keeps the table on screen (no spinner) while refreshing after a change.
  const loadLinks = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setLoadError(null);
    try {
      const data = await FoundationalEducationService.list(getAccessToken);
      // The backend already sends display order; sorting again keeps the table right regardless.
      setLinks(sortLinks(data));
    } catch (e) {
      setLoadError(e.message || 'Failed to load Foundational Education links');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  /* ── dialog ────────────────────────────────────────────────────────── */
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSaveError(null);
    setDialogOpen(true);
  };

  const openEdit = (link) => {
    setEditing(link);
    setForm(linkToForm(link));
    setFormErrors({});
    setSaveError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleChange = (field) => (e) => setField(field, e.target.value);

  const handleSave = async () => {
    const errors = validateLink(form);
    if (Object.values(errors).some(Boolean)) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      if (editing) {
        await FoundationalEducationService.update(
          getAccessToken,
          editing.id,
          formToRequest(form, editing.sortOrder),
        );
        notify('Link saved');
      } else {
        await FoundationalEducationService.create(getAccessToken, formToRequest(form, nextSortOrder(links)));
        notify('Link added');
      }
      setDialogOpen(false);
      loadLinks({ quiet: true });
    } catch (e) {
      setSaveError(e.message || 'Failed to save the link');
    } finally {
      setSaving(false);
    }
  };

  /* ── active toggle / reorder / delete ──────────────────────────────── */
  const toggleActive = async (link) => {
    const active = !link.active;
    setBusy(true);
    try {
      const saved = await FoundationalEducationService.update(
        getAccessToken,
        link.id,
        linkToRequest(link, { active }),
      );
      setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, ...(saved || {}), active } : l)));
      notify(active ? `"${link.titleEn}" is visible to members` : `"${link.titleEn}" is hidden from members`);
    } catch (e) {
      notify(e.message || 'Failed to update the link', 'error');
    } finally {
      setBusy(false);
    }
  };

  const move = async (index, direction) => {
    const changes = planMove(links, index, direction);
    if (changes.length === 0) return;
    setBusy(true);
    try {
      // One at a time, so a failure leaves as few links changed as possible.
      for (const { link, sortOrder } of changes) {
        await FoundationalEducationService.update(getAccessToken, link.id, linkToRequest(link, { sortOrder }));
      }
    } catch (e) {
      notify(e.message || 'Failed to reorder the links', 'error');
    } finally {
      await loadLinks({ quiet: true });
      setBusy(false);
    }
  };

  const runDelete = async () => {
    const link = confirmDelete;
    setConfirmBusy(true);
    try {
      await FoundationalEducationService.remove(getAccessToken, link.id);
      notify(`"${link.titleEn}" deleted`);
      setConfirmDelete(null);
      loadLinks({ quiet: true });
    } catch (e) {
      notify(e.message || 'Failed to delete the link', 'error');
      setConfirmDelete(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  /* ── render ────────────────────────────────────────────────────────── */
  return (
    <Box>
      {/* ── page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>Foundational Education</Typography>
            <Typography variant="body2" color="text.secondary">
              Amway's official, free Foundational Education materials. Once logged in, members see the active
              links on the Foundational Education page, in the navigation and footer, and on every store,
              content and events page.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Link
        </Button>
      </Box>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => loadLinks()}>Retry</Button>}
        >
          {loadError}
        </Alert>
      )}

      {/* ── links table ── */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : links.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {loadError ? 'Links could not be loaded.' : (
                      <Stack spacing={1.5} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          No Foundational Education links yet.
                        </Typography>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
                          Add link
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                links.map((l, index) => (
                  <TableRow key={l.id} hover data-testid={`link-row-${l.id}`}>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                        {l.titleEn}
                      </Typography>
                      {l.titleEs && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          {l.titleEs}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <UrlLink url={l.urlEn} />
                      {l.urlEs && (
                        <Box>
                          <UrlLink url={l.urlEs} prefix="ES: " />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!l.active}
                        onChange={() => toggleActive(l)}
                        disabled={busy}
                        slotProps={{ input: { role: 'switch', 'aria-label': `Active: ${l.titleEn}` } }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Move ${l.titleEn} up`}
                            disabled={busy || index === 0}
                            onClick={() => move(index, -1)}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Move ${l.titleEn} down`}
                            disabled={busy || index === links.length - 1}
                            onClick={() => move(index, 1)}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" aria-label={`Edit ${l.titleEn}`} onClick={() => openEdit(l)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Delete ${l.titleEn}`}
                          onClick={() => setConfirmDelete(l)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ══════════════════ Create / edit dialog ══════════════════════════ */}
      <Dialog
        open={dialogOpen}
        // Escape or a stray click outside would throw away what was typed, so only Cancel closes the editor
        onClose={(event, reason) => {
          if (reason !== 'escapeKeyDown' && reason !== 'backdropClick') closeDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{editing ? 'Edit Link' : 'New Foundational Education Link'}</DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, mb: -1 }}>
              English
            </Typography>
            <TextField
              label="Title (English)"
              required
              fullWidth
              value={form.titleEn}
              onChange={handleChange('titleEn')}
              error={!!formErrors.titleEn}
              helperText={formErrors.titleEn}
              inputProps={{ maxLength: TITLE_MAX }}
            />
            <TextField
              label="Description (English)"
              fullWidth
              multiline
              minRows={2}
              value={form.descriptionEn}
              onChange={handleChange('descriptionEn')}
              error={!!formErrors.descriptionEn}
              helperText={formErrors.descriptionEn || 'Plain text, shown under the title.'}
              inputProps={{ maxLength: DESCRIPTION_MAX }}
            />
            <TextField
              label="URL (English)"
              required
              fullWidth
              placeholder="https://..."
              value={form.urlEn}
              onChange={handleChange('urlEn')}
              error={!!formErrors.urlEn}
              helperText={formErrors.urlEn || 'Must start with http:// or https://.'}
              inputProps={{ maxLength: URL_MAX }}
            />

            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, mb: -1 }}>
              Spanish (optional: members reading in Spanish see the English text where it is blank)
            </Typography>
            <TextField
              label="Title (Spanish)"
              fullWidth
              value={form.titleEs}
              onChange={handleChange('titleEs')}
              error={!!formErrors.titleEs}
              helperText={formErrors.titleEs}
              inputProps={{ maxLength: TITLE_MAX }}
            />
            <TextField
              label="Description (Spanish)"
              fullWidth
              multiline
              minRows={2}
              value={form.descriptionEs}
              onChange={handleChange('descriptionEs')}
              error={!!formErrors.descriptionEs}
              helperText={formErrors.descriptionEs}
              inputProps={{ maxLength: DESCRIPTION_MAX }}
            />
            <TextField
              label="URL (Spanish)"
              fullWidth
              placeholder="https://..."
              value={form.urlEs}
              onChange={handleChange('urlEs')}
              error={!!formErrors.urlEs}
              helperText={formErrors.urlEs || 'Optional: leave blank to use the English link.'}
              inputProps={{ maxLength: URL_MAX }}
            />

            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setField('active', e.target.checked)} />}
              label="Active (shown to members)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (editing ? 'Save' : 'Add Link')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════ Delete confirm ═════════════════════════════════ */}
      <Dialog open={!!confirmDelete} onClose={() => !confirmBusy && setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete this link?</DialogTitle>
        <DialogContent>
          <Typography>
            "{confirmDelete?.titleEn}" will be deleted and members will no longer see it. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={confirmBusy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={runDelete} disabled={confirmBusy}>
            {confirmBusy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FoundationalEducationPage;
